/**
 * The header's Recent & Pinned control — the lens's ONE trigger (#2328 §1 item
 * 2, ROOT ruling 74071 §8).
 *
 * ── it is a TRIGGER, and there is only one surface ────────────────────────
 * Ruling 74071 §8: *"Recent & Pinned has one surface and one trigger. Move the
 * trigger to the header and remove the drawer-rail duplicate in coordination
 * with #2325. Do not create a second drawer."*
 *
 * So this opens the lens that already exists. `requestDrawerPanel` is the seam
 * `@solum/ui` publishes for exactly this — `CrumbBar.tsx:77` and
 * `approvals/InboxPage.tsx:85` already reveal a lens the same way — so the
 * header needs no import of the drawer, no second store and no knowledge of
 * what the panel renders. The panel key `nav` is `ShellDrawer`'s own
 * registration for *"Recent and pinned"*, and the two accessible names here are
 * that panel's own `openLabel` / `closeLabel`, so the control and the lens
 * cannot describe themselves differently.
 *
 * ── the rail duplicate is GONE, and this is why this is a DISCLOSURE ───────
 * The grant arrived in mailbox #1863 comment 74155 — *"#2328 owns the atomic
 * header-trigger move and the `ShellDrawer.tsx` rail-entry removal"* — and the
 * rail entry is now `hostTriggered`, so the drawer draws no control for this
 * lens at any width. That is what makes the toggle load-bearing rather than
 * polish: the rail button was the only way a MOUSE could close this panel on a
 * desktop, because the docked column has no close control of its own (only the
 * compact sheet does). A one-way opener would have removed a capability while
 * claiming to move one, which is the #1466 finding-1 shape exactly.
 *
 * So the control reads the drawer's own published open state rather than
 * keeping a copy: `useDrawerPanelOpen('nav')`. Ruling §8's *"one panel, one
 * surface, one state"* is what forbids the alternative — a trigger with its own
 * idea of open/closed drifts from the panel it names the first time anything
 * else closes it (Escape, the sheet's close button, a persona change).
 */
import { History } from 'lucide-react'
import { Button, requestDrawerPanel, useDrawerPanelOpen } from '@solum/ui'
import s from './Topbar.module.css'

/* The SHARED Button, not a raw one. `experience-adoption.test.ts` counts raw
   native controls per host, and the portal's own drawer launcher carries the
   sentence this follows: *"the shared Button, because 6 is a number somebody
   worked down and a new host control is exactly where it creeps back"*. The
   console's raw count is far larger and is a separate adoption; this packet
   does not add to it. */
export function RecentPinnedTrigger() {
  const open = useDrawerPanelOpen('nav')
  return (
    <Button size="sm" variant="ghost" className={s.iconAction}
      onClick={() => requestDrawerPanel('nav', { toggle: true })}
      aria-expanded={open}
      aria-label={open ? 'Close recently opened records' : 'Open recently opened records'}
      title="Recently opened"
      data-testid="recent-pinned-trigger"
      icon={<History size={16} />} />
  )
}
